import {FC, useState, useEffect, useRef} from 'react'
import Select from 'react-select'
import { Spinner } from 'react-bootstrap';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';

import { adminAPI } from '../../../api';
import { convertToDateTime, StyledDataGrid, dataGridCellClassName, dataGridHeaderClassName, downloadObjects } from '../../../utils'

import BootstrapDateRangePicker from '../../../components/BootstrapDateRangePicker'

import { MarkdownWithLinks, renderUserMessage} from '../../../components/MarkdownWithLinks'

import {toTitleCase, decodeUnicode} from '../../../utils'
import {FEEDBACK_CATEGORIES, FEEDBACK_CATEGORIES_FOR_IMAGE} from '../../../models'
import {UserType} from '../../../models'

import { Bar } from 'react-chartjs-2';

interface Props {
}
interface ChartData {
  labels: string[];
  data: { [key: string]: number[] };
}

const Messages : React.FC<Props> = ({}) => {

  const [loaded, setLoaded] = useState<boolean>(false)
  const [hasNoData, setHasNoData] = useState<boolean>(false)

  const [refresh, setRefresh] = useState<string>()
  const [modelIDs, setModelIDs] = useState<any[]>([])

  const projectId = "fanar"
  const [selectedLLMs, setSelectedLLMs] =  useState<any[]>([])
  const [selectedUserTypes, setSelectedUserTypes] = useState<any[]>([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [timezoneOffset, setTimezoneOffset] = useState<number>()

  const [stats, setStats] = useState<any | undefined>()

  const [messages, setMessages] = useState<any[]>()

  const chartRef = useRef(null);
  const [responsesByDate, setResponsesByDate] = useState<ChartData>()
  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: { label: string; data: number[] }[];
  } | null>(null);

  const refreshRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (refreshRef.current) {
      clearInterval(refreshRef.current);
    }

    if (refresh && !isNaN(parseInt(refresh))) {
      refreshRef.current = setInterval(() => {
        loadModelIDs()
        load()
      }, parseInt(refresh) * 1000)
    }

    return () => {
      if( refreshRef.current ){
        clearInterval(refreshRef.current)
        refreshRef.current = null
      }
    }
  }, [refresh])

  useEffect(() => {
    loadModelIDs()
  }, [startDate, endDate, timezoneOffset])

  useEffect(() => {
    load()
  }, [startDate, endDate, timezoneOffset, projectId, selectedLLMs, selectedUserTypes])


  async function loadModelIDs () {
    if( startDate && endDate && timezoneOffset ){
      let {data: data} = await adminAPI.getChatMessageModelIDs(startDate, endDate, timezoneOffset)
      setModelIDs(data['data'])
    }
  }


  useEffect(() => { 
    if (responsesByDate) {
      const datasets = Object.entries(responsesByDate.data).map(([label, data]) => ({
        label,
        data,
      }));

      const data = {
        labels: responsesByDate.labels,
        datasets,
      };

      setChartData(data);
    } else {
      setChartData(null);
    }
  },[responsesByDate])

  const handleDateSelection = (startDate: string, endDate:string, timezoneOffset:number) => {
    setStartDate(startDate)
    setEndDate(endDate)
    setTimezoneOffset(timezoneOffset)
  }

  async function load(){

    if( startDate && endDate && timezoneOffset ){
      setLoaded(false)
      let {data: data} = await adminAPI.getChatMessages(startDate, endDate, timezoneOffset, projectId, selectedLLMs, selectedUserTypes)
      if( data['data']['data'] ){
        setMessages(data['data']['data'])
        setStats(data['data']['stats'])
        setHasNoData(false)
      }else{
        setHasNoData(true)
      }
      setLoaded(true)
    }
  }

  const handleDownload = () => {
    if( messages ){
      downloadObjects('csv', messages, 'messages')
    }
  }
  
  const scroll = (text) => {
    return <div className="overflow-y-auto" style={{maxHeight: '142px'}}>{text}</div>
  }

  const columnsForMessages: GridColDef[] = [
    { field: 'chat_deleted_dt', headerName: 'Chat Status', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 50,
      renderCell: (params) => {
        return params.value ? 'deleted' : 'active'
      }
    },
    { field: "user_id", headerName: 'User ID', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 150},
    { field: "user_type", headerName: 'User Type', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 80, valueGetter: (value) => value || 'user'},
    { field: "prompt_id", headerName: 'Prompt ID', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 80},
    { field: 'prompt', headerName: 'Prompt', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName + ' min-w-200px mw-300px', minWidth: 200,
      renderCell: (params) => {
        return scroll(renderUserMessage(params.value))
      }
    },
    { field: 'prompt_filtered', headerName: 'Prompt Filtered', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 50},
    { field: 'translated_prompt', headerName: 'Translated Prompt', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName + ' min-w-200px mw-350px', minWidth: 200,
      renderCell: (params) => {
        return scroll(params.value)
      }
    },
    { field: 'prompt_on', headerName: 'Prompt On', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120,
      renderCell: (params) => {
        return convertToDateTime(params.value)
      }
    },
    { field: 'prompt_category', headerName: 'Prompt Category', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120},
    { field: 'prompt_type', headerName: 'Prompt Type', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120},
    { field: 'prompt_topic', headerName: 'Prompt Topic', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120},
    { field: 'prompt_generation_type', headerName: 'Generation Type', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 50},
    { field: 'response', headerName: 'Response', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 250,
      renderCell: (params) => {
        return scroll(<MarkdownWithLinks>{decodeUnicode(params.value)}</MarkdownWithLinks>)
      }
    },
    { field: 'response_filtered', headerName: 'Response Filtered', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 50},
    { field: 'translated_response', headerName: 'Translated Response', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120,
      renderCell: (params) => {
        return scroll(decodeUnicode(params.value))
      }
    },
    { field: 'response_on', headerName: 'Response On', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120,
      renderCell: (params) => {
        return convertToDateTime(params.value)
      }
    },
    { field: 'response_total_tokens', headerName: 'Total Tokens', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 80},
    { field: 'response_completion_tokens', headerName: 'Completion Tokens', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 80},
    { field: 'response_model_id', headerName: 'LLM', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 200},
    { field: 'response_order', headerName: 'Regenerated', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 120,
      renderCell: (params) => {
        return params.value > 1 ? 'true' : 'false'
      }
    },
    { field: 'reaction', headerName: 'Reaction', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 80},
    { field: 'reaction_on_regenerated_response', headerName: 'Rate on Regerated Response', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 80},
    { field: 'feedback_category', headerName: 'Feedback Category', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 150},
    { field: 'feedback', headerName: 'Feedback', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 250, 
      renderCell: (params) => {
        return scroll(<MarkdownWithLinks>{decodeUnicode(params.value)}</MarkdownWithLinks>)
      }
    }
  ]

  const columnVisibilitiesForMessages = {
    translated_prompt: false,
    translated_response: false
  }

  return (
    <div className="row">
      <div className="col-12 d-flex flex-row align-items-center justify-content-between">
        <div className="d-flex flex-row align-items-center">
          <BootstrapDateRangePicker onDateSelect={handleDateSelection} disabled={!loaded}/>
          <Select  
              className='react-select-styled ms-4 w-250px' 
              classNamePrefix='react-select' 
              options={modelIDs.map(modelId => ({'label':modelId, 'value':modelId}))} 
              placeholder='LLMs' 
              isMulti={true}
              isClearable={true}
              isSearchable={true}
              isDisabled={!loaded}
              onChange={(selectedOptions) => setSelectedLLMs(selectedOptions?.map(option => option.value))}
          />
          <Select  
              className='react-select-styled ms-4 w-250px' 
              classNamePrefix='react-select' 
              options={Object.values(UserType).filter(type => type != UserType.LOAD_TESTER).map(type => ({'label':toTitleCase(type), 'value':type}))} 
              placeholder='User Types' 
              isMulti={true}
              isClearable={true}
              isSearchable={true}
              isDisabled={!loaded}
              onChange={(selectedOptions) => setSelectedUserTypes(selectedOptions?.map(option => option.value))}
          />
          {!loaded && (
            <Spinner animation='border' className='ms-4'/>
          )}
        </div>
        <div className="d-flex flex-row align-items-center">
          <span>Refresh</span>
          <Select  
            className='react-select-styled ms-4 w-150px' 
            classNamePrefix='react-select' 
            options={[
              {'label':'Off', 'value':''}, 
              {'label':'Every 30 seconds', 'value':'30'}, 
              {'label':'Every 1 minute', 'value':'60'}, 
              {'label':'Every 5 minutes', 'value':'300'},
              {'label':'Every 10 minutes', 'value':'600'}
            ]} 
            placeholder='Off' 
            onChange={(selectedOption) => setRefresh(selectedOption?.value)}
          />
        </div>
      </div>
      {hasNoData 
      ?
        <div className="col-12 mt-5">
          <div className="alert alert-primary">No data</div>
        </div>
      :
      <>
        <div className="col-12 mb-5">
          {!hasNoData && responsesByDate && (
          <Bar
            ref={chartRef}
            data={chartData || { labels: [], datasets: [] }}
            height="35"
            options={{
              plugins: {
                tooltip: {
                  mode: 'index',
                },
                title: {
                  display: false,
                },
              },
              scales: {
                x: {
                  ticks: {
                    maxTicksLimit: 10,
                    display: true,
                  },
                  display: true,
                  stacked: true,
                },
                y: {
                  beginAtZero: true,
                  stacked: true,
                  display: false,
                },
              },
              responsive: true,
            }}
          />
          )}
        </div>
        <div className="col-4">
          <div className="card">
            <div className="card-body pt-5">
              <div className="d-flex flex-wrap">
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 number_of_responses number_of">{!hasNoData && stats && stats['number_of_users']? stats['number_of_users'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># users</span>
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 number_of_responses number_of">{!hasNoData && stats && stats['number_of_chats']? stats['number_of_chats'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># chats</span>
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 number_of_responses number_of">{!hasNoData && stats && stats['number_of_prompts']? stats['number_of_prompts'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># prompts</span>
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 number_of_responses number_of">{!hasNoData && stats && stats['number_of_responses']? stats['number_of_responses'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># responses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-8">
            <div className="card">
            <div className="card-body pt-5">
              <div className="d-flex flex-wrap">
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 ">{!hasNoData && stats && stats['number_of_regenerated_responses'] ? stats['number_of_regenerated_responses'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># regenerated responses</span>
                </div>
                <div className="border">
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 ">{!hasNoData && stats && stats['number_of_reactions_for_regenerated_responses']['like'] ? stats['number_of_reactions_for_regenerated_responses']['like'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># good responses</span>
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 ">{!hasNoData && stats && stats['number_of_reactions_for_regenerated_responses']['dislike']? stats['number_of_reactions_for_regenerated_responses']['dislike'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># bad responses</span>
                </div>
                <div className="border">
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 ">{!hasNoData && stats && stats['number_of_reactions_for_regenerated_responses']['better']? stats['number_of_reactions_for_regenerated_responses']['better'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># better responses</span>
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 ">{!hasNoData && stats && stats['number_of_reactions_for_regenerated_responses']['worse'] ? stats['number_of_reactions_for_regenerated_responses']['worse'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># worse responses</span>
                </div>
                <div className="mx-auto d-flex flex-column p-0">
                  <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2 ">{!hasNoData && stats && stats['number_of_reactions_for_regenerated_responses']['same'] ? stats['number_of_reactions_for_regenerated_responses']['same'].toLocaleString() : 0}</span>
                  <span className=" opacity-75 pt-1 fw-semibold fs-6"># same responses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 mt-5">
          <div className="card">
            <div className="card-body pt-5">
              {['text','image'].map((generationType, index) => (
                <div className={`${generationType == 'image' && 'mt-5'}`}>
                  <div className="fw-semibold fs-6 mb-5">{toTitleCase(generationType)} Generation</div>
                  <div className="d-flex flex-wrap">
                    <div className="mx-auto d-flex flex-column p-0">
                      <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2">{!hasNoData && stats && stats.number_of_reactions?.[generationType]?.like ? stats['number_of_reactions'][generationType]['like'].toLocaleString() : 0}</span>
                      <span className=" opacity-75 pt-1 fw-semibold fs-6"># good responses</span>
                    </div>
                    <div className="mx-auto d-flex flex-column p-0">
                      <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2">{!hasNoData && stats && stats.number_of_reactions?.[generationType]?.dislike ? stats['number_of_reactions'][generationType]['dislike'].toLocaleString() : 0}</span>
                      <span className=" opacity-75 pt-1 fw-semibold fs-6"># bad responses</span>
                    </div>
                    <div className="border">
                    </div>
                    {(generationType == 'image'? FEEDBACK_CATEGORIES_FOR_IMAGE : FEEDBACK_CATEGORIES).map((category, index) => (
                      <div className="mx-auto d-flex flex-column p-0" key={index}>
                        <span className="fs-2hx fw-bold  me-2 lh-1 ls-n2f">{!hasNoData && stats && stats['number_of_feedback_categories'][generationType][category] ? stats['number_of_feedback_categories'][generationType][category].toLocaleString() : 0}</span>
                        <span className=" opacity-75 pt-1 fw-semibold fs-8 pt-2 mw-100px">{category.replaceAll('_',' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-12 mt-5">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title"></h3>
              <div className="card-toolbar">
                  <button  className="btn download-messages-btn btn-sm btn-light cursor-pointer" disabled={hasNoData} onClick={handleDownload}>
                      Download
                  </button>
              </div>
            </div>
            <div className="card-body pt-5">
              {!hasNoData && messages && (
                <StyledDataGrid
                  getRowId={(row) => row.response_id || row.prompt_id}
                  rows={messages}
                  columns={columnsForMessages}
                  disableColumnMenu={false}
                  getRowHeight={() => 'auto'}
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 50 },
                    },
                    columns: {
                      columnVisibilityModel: columnVisibilitiesForMessages,
                    }
                  }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                    },
                  }}
                  pageSizeOptions={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 100]}
                />
              )}
            </div>
          </div>
        </div>
      </>
      }
    </div>
    )
}

export default Messages